import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "./AuthContext";
import ConsoleShell, { Breadcrumb } from "./ConsoleShell";
import { ArrowRight, Download, Upload, Loader2, AlertTriangle, MoreHorizontal, Trash2, Plus, Inbox, Pencil, Users, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import NewEngagementModal from "./NewEngagementModal";

// Category palette — keyed for fast visual scanning during a live demo.
// Muted on purpose so it reads as a tag, not a status pill.
const CATEGORY_STYLE = {
  Pharma:           { bg: "#0F1825", fg: "#FFFFFF" },
  Immunotherapy:    { bg: "#C84C21", fg: "#FFFFFF" },
  "Gene therapy":   { bg: "#2D5F66", fg: "#FFFFFF" },
  Diagnostics:      { bg: "#7A6244", fg: "#FFFFFF" },
};

// Demo-priority order: launch + post-launch cases lead (V2E's primary ICP focus),
// then immunotherapy / diagnostics, with pre-launch + gene-therapy as proof-of-breadth
// at the bottom. Lower number = higher position.
const PRIORITY_ORDER = (e) => {
  const detail = (e.demo_category_detail || "").toLowerCase();
  if (detail.includes("launch") && !detail.includes("pre-launch")) return 1;     // launch / post-launch
  if (detail.includes("marketed")) return 2;                                      // marketed · adoption
  if (e.demo_category === "Immunotherapy") return 3;                              // immunotherapy any phase
  if (e.demo_category === "Diagnostics") return 4;                                // diagnostics
  if (detail.includes("pivotal")) return 5;                                       // pivotal Phase 3
  if (detail.includes("pre-launch")) return 6;                                    // pre-launch ultra-rare
  if (e.demo_category === "Gene therapy") return 7;                               // pre-clinical gene therapy
  return 99;
};

export default function ConsoleEngagements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const { user } = useAuth();
  // Reviewers must never see engineering-facing tools (JSON bundle export /
  // import, delete engagement). These are strictly operator/admin actions —
  // presenting them to Medical Affairs reviewers signals engineering
  // clutter, not product polish.
  const isAdminRole = ((user?.role || "").toLowerCase() === "admin"
                    || (user?.role || "").toLowerCase() === "operator");
  const isStrictAdmin = (user?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/console/engagements`);
        const sorted = [...(data.engagements || [])].sort((a, b) => PRIORITY_ORDER(a) - PRIORITY_ORDER(b));
        setItems(sorted);
      } catch {
        /* handled by RequireAuth */
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadKey]);

  const liveItems = items.filter((e) => e.kind === "live");
  const demoItems = items.filter((e) => e.kind !== "live");
  // Drop the card immediately after a confirmed delete, then re-sync with the server.
  const removeItem = (id) => {
    setItems((prev) => prev.filter((e) => e.id !== id));
    setReloadKey((k) => k + 1);
  };
  const updateItem = (updated) => {
    setItems((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
  };
  const [editing, setEditing] = useState(null);

  // Business Development accounts have no engagement console — Outreach and the guided demo only.
  if ((user?.role || "").toLowerCase() === "bd") return <Navigate to="/outreach" replace />;

  return (
    <ConsoleShell breadcrumb={<Breadcrumb items={[{ label: "Engagements" }]} />}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="page-console-engagements">
        <div className="mb-10 flex items-start justify-between gap-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Active engagements</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0F1825] mt-3 leading-[1.1] tracking-tight">Engagements console.</h1>
            <p className="mt-4 text-[#5C6470] text-[15px] leading-relaxed max-w-2xl">
              Each engagement is scoped around an asset, indication, and lifecycle phase. Live client engagements sit above the illustrative demo cases.
            </p>
          </div>
          {isAdminRole && (
            <div className="flex items-start gap-3 shrink-0">
              {isStrictAdmin && (
                <>
                  <Link
                    to="/console/analytics"
                    className="inline-flex items-center gap-2 border border-[#E8E1D2] bg-white hover:border-[#C84C21] text-[#0F1825] text-[11px] font-mono uppercase tracking-[0.2em] px-3.5 py-2 transition-colors"
                    data-testid="analytics-link"
                  >
                    <BarChart3 size={13} />
                    Analytics
                  </Link>
                  <Link
                    to="/console/users"
                    className="inline-flex items-center gap-2 border border-[#E8E1D2] bg-white hover:border-[#C84C21] text-[#0F1825] text-[11px] font-mono uppercase tracking-[0.2em] px-3.5 py-2 transition-colors"
                    data-testid="manage-users-link"
                  >
                    <Users size={13} />
                    Manage users
                  </Link>
                </>
              )}
              <Link
                to="/console/requests"
                className="inline-flex items-center gap-2 border border-[#E8E1D2] bg-white hover:border-[#C84C21] text-[#0F1825] text-[11px] font-mono uppercase tracking-[0.2em] px-3.5 py-2 transition-colors"
                data-testid="briefing-requests-link"
              >
                <Inbox size={13} />
                Briefing requests
              </Link>
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] text-white text-[11px] font-mono uppercase tracking-[0.2em] px-3.5 py-2 transition-colors"
                data-testid="new-engagement-button"
              >
                <Plus size={13} />
                New engagement
              </button>
              <ImportBundleButton onImported={() => setReloadKey((k) => k + 1)} />
            </div>
          )}
        </div>

        <NewEngagementModal
          open={newOpen}
          onClose={() => setNewOpen(false)}
          onCreated={() => setReloadKey((k) => k + 1)}
        />
        {editing && (
          <NewEngagementModal
            key={editing.id}
            open
            engagement={editing}
            onClose={() => setEditing(null)}
            onUpdated={updateItem}
          />
        )}

        {loading ? (
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#5C6470]">Loading engagements…</div>
        ) : (
          <>
            {/* LIVE CLIENT ENGAGEMENTS */}
            {liveItems.length > 0 && (
              <div className="mb-12" data-testid="live-engagements-section">
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="w-2 h-2 rounded-full bg-[#C84C21]"></span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0F1825]">
                    Live client engagements
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">
                    {liveItems.length} active
                  </span>
                </div>
                <div className="space-y-3" data-testid="live-engagements-grid">
                  {liveItems.map((e) => (
                    <EngagementCard key={e.id} e={e} live isAdmin={isAdminRole} onDeleted={() => removeItem(e.id)} onEdit={() => setEditing(e)} />
                  ))}
                </div>
              </div>
            )}

            {/* ILLUSTRATIVE DEMO ENGAGEMENTS */}
            {demoItems.length > 0 && (
              <div data-testid="demo-engagements-section">
                <div className="flex items-baseline gap-4 mb-5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-[#A09680]"></span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#5C6470]">
                    Illustrative demo engagements
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">
                    {demoItems.length} cases
                  </span>
                </div>
                <div className="space-y-3" data-testid="engagements-grid">
                  {demoItems.map((e) => (
                    <EngagementCard key={e.id} e={e} isAdmin={isAdminRole} onDeleted={() => removeItem(e.id)} onEdit={() => setEditing(e)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ConsoleShell>
  );
}

// Single card — same structure for live and demo, with subtle visual
// differentiation when `live` is true (rust left-border + "LIVE" pill).
function EngagementCard({ e, live = false, isAdmin = false, onDeleted, onEdit }) {
  const anonym = e.client_anonymised;
  const catStyle = CATEGORY_STYLE[e.demo_category];
  const cardClass = live
    ? "block bg-white border border-[#E8E1D2] border-l-[3px] border-l-[#C84C21] hover:shadow-md hover:border-[#C84C21] transition-all p-6 group relative"
    : "block bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 hover:shadow-sm transition-all p-6 group";

  return (
    <Link
      to={`/console/engagements/${e.id}`}
      className={cardClass}
      data-testid={`engagement-card-${e.id}`}
    >
      {live && (
        <span
          className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.22em] bg-[#C84C21] text-white px-2 py-[2px]"
          data-testid={`engagement-live-pill-${e.id}`}
        >
          Live client
        </span>
      )}
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-3 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]">{e.client_label}</span>
            {e.demo_category && (
              <>
                <span className="text-[#E8E1D2]">·</span>
                <span
                  className="font-mono text-[9.5px] uppercase tracking-[0.18em] px-2 py-[3px]"
                  style={catStyle ? { backgroundColor: catStyle.bg, color: catStyle.fg } : undefined}
                  data-testid={`engagement-category-${e.id}`}
                >
                  {e.demo_category}
                </span>
                {e.demo_category_detail && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#5C6470]">{e.demo_category_detail}</span>
                )}
              </>
            )}
          </div>
          <h3 className="font-serif text-2xl text-[#0F1825] leading-snug">
            {anonym ? "Confidential engagement" : e.brand}
          </h3>
          <div className="mt-1 text-[14px] text-[#5C6470]">
            {anonym ? e.mechanism : `${e.asset} · ${e.mechanism}`}
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-[12.5px]">
            <Meta label="Therapeutic area" value={e.ta} />
            <Meta label="Indication" value={e.indication} />
            <Meta label="Lifecycle" value={e.lifecycle_phase} />
          </div>
          <div className="mt-5 flex items-center gap-5 text-[11px] font-mono uppercase tracking-[0.16em] text-[#5C6470]">
            <span><span className="text-[#0F1825] font-medium">{e.signal_count}</span> signals</span>
            {isAdmin && <EngagementCardMenu engagement={e} onDeleted={onDeleted} onEdit={onEdit} />}
          </div>
        </div>
        <div className="hidden md:flex items-center text-[#C84C21] opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight size={20} />
        </div>
      </div>
    </Link>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-1">{label}</div>
      <div className="text-[#0F1825] leading-snug">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Environment transfer — Export bundle · Delete engagement · Import bundle.
// The card exposes a small ⋯ menu with Export + Delete; the page header
// exposes the Import button. Delete on a `kind=live` engagement requires
// typing the brand name to confirm (defence-in-depth on top of the backend
// ?force=true guard).
// ─────────────────────────────────────────────────────────────────────────

function EngagementCardMenu({ engagement, onDeleted, onEdit }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (menuRef.current && !menuRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleExport = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setOpen(false);
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/console/admin/export-engagement/${engagement.id}`,
        { responseType: "blob", timeout: 120000 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `v2e-engagement-bundle-${slugifyBrand(engagement.brand)}-${engagement.id.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bundle exported", { description: a.download });
    } catch (err) {
      toast.error("Export failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setOpen(false);
    setDeleteOpen(true);
  };

  return (
    <div ref={menuRef} className="ml-auto relative">
      <button
        type="button"
        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center border border-[#E8E1D2] hover:border-[#C84C21]/60 hover:text-[#C84C21] text-[#5C6470] px-1.5 py-1 transition-colors"
        aria-label="Engagement actions"
        aria-expanded={open}
        data-testid={`card-menu-${engagement.id}`}
      >
        {exporting ? <Loader2 size={13} className="animate-spin" /> : <MoreHorizontal size={13} />}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 min-w-[200px] bg-white border border-[#E8E1D2] shadow-lg py-1"
          role="menu"
          data-testid={`card-menu-panel-${engagement.id}`}
        >
          <button
            type="button"
            onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setOpen(false); onEdit?.(); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[11.5px] font-mono uppercase tracking-[0.16em] text-[#0F1825] hover:bg-[#FAF7F1] transition-colors"
            role="menuitem"
            data-testid={`card-menu-edit-${engagement.id}`}
          >
            <Pencil size={12} className="text-[#5C6470]" />
            Edit engagement
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[11.5px] font-mono uppercase tracking-[0.16em] text-[#0F1825] hover:bg-[#FAF7F1] transition-colors"
            role="menuitem"
            data-testid={`card-menu-export-${engagement.id}`}
          >
            <Download size={12} className="text-[#5C6470]" />
            Export bundle
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[11.5px] font-mono uppercase tracking-[0.16em] text-[#C84C21] hover:bg-[#FDF1E6] transition-colors border-t border-[#F0EBDC]"
            role="menuitem"
            data-testid={`card-menu-delete-${engagement.id}`}
          >
            <Trash2 size={12} />
            Delete engagement
          </button>
        </div>
      )}
      {deleteOpen && (
        <DeleteEngagementModal
          engagement={engagement}
          onCancel={() => setDeleteOpen(false)}
          onDone={() => { setDeleteOpen(false); onDeleted?.(); }}
        />
      )}
    </div>
  );
}

function DeleteEngagementModal({ engagement, onCancel, onDone }) {
  const isLive = engagement?.kind === "live";
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const requiredText = engagement?.brand || "";
  // Case-insensitive + trimmed compare so "CENERIMOD", "cenerimod", and
  // "Cenerimod" all pass. Also strip common trademark punctuation that
  // brands may include (™ ® ©) so typing 'Cenerimod' still gates a brand
  // stored as 'Cenerimod™'.
  const _norm = (s) => (s || "")
    .trim()
    .toLowerCase()
    .replace(/[™®©]/g, "")
    .replace(/\s+/g, " ");
  const gatePassed = !isLive || _norm(confirmText) === _norm(requiredText);

  const stop = (ev) => { ev.preventDefault(); ev.stopPropagation(); };

  const doDelete = async () => {
    if (!gatePassed) return;
    setBusy(true);
    try {
      const url = `${API}/console/engagements/${engagement.id}${isLive ? "?force=true" : ""}`;
      const { data } = await axios.delete(url, { timeout: 60000 });
      const c = data?.counts || {};
      const bits = [
        `${c.signals || 0} signals`,
        `${c.findings || 0} findings`,
        `${c.triangulation_runs || 0} triangulation runs`,
        `${c.actions || 0} actions`,
        `${c.deliverables || 0} deliverables`,
        `${c.files_deleted || 0} files`,
      ].join(" · ");
      toast.success("Engagement deleted", { description: bits });
      onDone?.();
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#0F1825]/60 flex items-center justify-center px-4 normal-case tracking-normal font-sans"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      data-testid={`delete-modal-${engagement.id}`}
    >
      <div
        className="bg-white border w-full max-w-lg p-7 relative"
        style={{ borderColor: isLive ? "#C84C21" : "#E8E1D2" }}
        onClick={stop}
      >
        {isLive && (
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ backgroundColor: "#C84C21" }}
          />
        )}
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle
            size={22}
            className="mt-1 shrink-0"
            style={{ color: isLive ? "#C84C21" : "#8C3A12" }}
          />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">
              {isLive ? "Danger · live engagement" : "Confirm deletion"}
            </div>
            <h2 className="font-serif text-[22px] text-[#0F1825] leading-tight">
              Delete <span className="italic">{engagement.brand}</span>?
            </h2>
            <div className="text-[12px] text-[#5C6470] mt-0.5">{engagement.client_label}</div>
          </div>
        </div>

        <div className="text-[13px] text-[#5C6470] leading-relaxed mb-4">
          This permanently deletes the engagement <span className="font-mono text-[12px] text-[#0F1825]">{engagement.id}</span> and cascades through every associated document — signals, findings, triangulation runs, perplexity runs, gap cells, actions, deliverables, methodology, and sources — as well as any source PDFs and deliverable files on disk. This action cannot be undone.
        </div>

        {isLive && (
          <div className="mb-5 bg-[#FDF1E6] border border-[#E8C19A] p-3.5">
            <div className="text-[12.5px] text-[#8C3A12] leading-relaxed mb-2.5">
              This is a <b>live client engagement</b>. Consider running <b>Export bundle</b> first — you can re-import the bundle later if you change your mind. Type the asset brand name below to enable deletion.
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#8C3A12] mb-1.5">
              Type <span className="bg-white px-1 text-[#0F1825] normal-case tracking-normal font-medium" style={{ letterSpacing: 0 }}>{requiredText}</span> to confirm:
            </div>
            <input
              type="text"
              value={confirmText}
              onChange={(ev) => setConfirmText(ev.target.value)}
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-white border border-[#E8C19A] px-3 py-2 text-[14px] text-[#0F1825] font-mono focus:outline-none focus:border-[#C84C21]"
              placeholder={requiredText}
              data-testid="delete-confirm-input"
            />
            <div className="mt-1.5 text-[10.5px] text-[#8C3A12] font-mono">
              Case-insensitive. Trademark symbols (™ ® ©) are stripped.
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="border border-[#E8E1D2] text-[#5C6470] hover:text-[#0F1825] hover:border-[#0F1825] text-[11.5px] font-mono uppercase tracking-[0.2em] px-4 py-2 transition-colors disabled:opacity-60"
            data-testid={`delete-modal-cancel-${engagement.id}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doDelete}
            disabled={busy || !gatePassed}
            className="bg-[#C84C21] hover:bg-[#a63e1a] text-white text-[11.5px] font-mono uppercase tracking-[0.2em] px-4 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid={`delete-modal-confirm-${engagement.id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {busy ? "Deleting…" : (isLive ? "Force delete" : "Delete engagement")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function slugifyBrand(brand) {
  return String(brand || "engagement")
    .toLowerCase()
    .replace(/[™®©]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}

function ImportBundleButton({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null); // {bundle, response}
  const inputRef = useRef(null);

  const handleFile = async (file, force) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      let bundle;
      try {
        bundle = JSON.parse(text);
      } catch {
        toast.error("Not a valid JSON file");
        return;
      }
      if (!bundle || bundle.bundle_version !== "1.0") {
        toast.error("Not a Voice2Evidence bundle (v1.0)");
        return;
      }
      const url = `${API}/console/admin/import-engagement${force ? "?force=true" : ""}`;
      const res = await axios.post(url, bundle, {
        headers: { "Content-Type": "application/json" },
        timeout: 300000,
        // 409 conflict is expected → don't throw
        validateStatus: (s) => s < 500,
      });
      if (res.status === 409 && res.data?.conflict) {
        setConflict({ bundle, response: res.data });
        return;
      }
      if (res.status >= 400) {
        toast.error("Import failed", { description: res.data?.detail || `HTTP ${res.status}` });
        return;
      }
      const c = res.data?.counts || {};
      toast.success(force ? "Bundle overwritten" : "Bundle imported", {
        description: `${c.signals || 0} signals · ${c.findings || 0} findings · ${c.actions || 0} actions · ${c.deliverables || 0} deliverables · ${c.methodology || 0} methodology · ${c.source_files || 0} source files`,
      });
      setConflict(null);
      onImported?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Import failed";
      toast.error("Import failed", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = (ev) => {
    const file = ev.target.files?.[0];
    if (file) handleFile(file, false);
    ev.target.value = ""; // allow re-selecting the same file
  };

  const onForceOverwrite = async () => {
    if (!conflict?.bundle) return;
    // Rebuild a File-like using the raw bundle object
    const blob = new Blob([JSON.stringify(conflict.bundle)], { type: "application/json" });
    const file = new File([blob], "bundle.json", { type: "application/json" });
    await handleFile(file, true);
  };

  return (
    <div className="shrink-0" data-testid="import-bundle-wrap">
      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={onFileChange}
        data-testid="import-bundle-file-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 bg-white border border-[#E8E1D2] hover:border-[#C84C21] hover:text-[#C84C21] text-[11px] font-mono uppercase tracking-[0.2em] text-[#0F1825] px-3.5 py-2 transition-colors disabled:opacity-60"
        data-testid="import-bundle-button"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {busy ? "Importing…" : "Import bundle"}
      </button>
      <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#A09680] text-right">
        Environment transfer · JSON
      </div>

      {conflict && (
        <ImportConflictModal
          conflict={conflict}
          onCancel={() => setConflict(null)}
          onForce={onForceOverwrite}
          busy={busy}
        />
      )}
    </div>
  );
}

function ImportConflictModal({ conflict, onCancel, onForce, busy }) {
  const r = conflict.response || {};
  const existing = r.existing_engagement || {};
  const existingCounts = r.existing_counts || {};
  const bundleCounts   = r.bundle_counts || {};
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F1825]/60 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="import-conflict-modal"
    >
      <div className="bg-white border border-[#E8E1D2] w-full max-w-2xl p-7">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={22} className="text-[#C84C21] mt-1 shrink-0" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">Conflict · engagement exists</div>
            <h2 className="font-serif text-2xl text-[#0F1825] leading-tight">Overwrite the existing engagement on this environment?</h2>
          </div>
        </div>
        <div className="text-[13px] text-[#5C6470] leading-relaxed mb-5">
          An engagement with id <span className="font-mono text-[12px] text-[#0F1825]">{r.engagement_id}</span> already exists here.
          Forcing overwrite deletes every associated document (signals, findings, triangulation runs, perplexity runs, gap cells, actions, deliverables, methodology, sources) scoped to this engagement id, then re-inserts the bundle exactly.
        </div>

        <div className="mb-4 bg-[#FAF7F1] border border-[#E8E1D2] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">On this environment</div>
          <div className="text-[13.5px] text-[#0F1825] font-medium">{existing.client_label}</div>
          <div className="text-[12.5px] text-[#5C6470] mt-0.5">{existing.asset}</div>
          <div className="text-[12.5px] text-[#5C6470]">{existing.indication}</div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="import-conflict-counts">
          <CountsBlock label="Existing (will be deleted)" counts={existingCounts} accent="#A09680" />
          <CountsBlock label="Bundle (will be inserted)"   counts={bundleCounts}   accent="#C84C21" />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="border border-[#E8E1D2] text-[#5C6470] hover:text-[#0F1825] hover:border-[#0F1825] text-[11.5px] font-mono uppercase tracking-[0.2em] px-4 py-2 transition-colors disabled:opacity-60"
            data-testid="import-conflict-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onForce}
            disabled={busy}
            className="bg-[#C84C21] hover:bg-[#a63e1a] text-white text-[11.5px] font-mono uppercase tracking-[0.2em] px-4 py-2 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="import-conflict-force"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
            {busy ? "Overwriting…" : "Force overwrite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CountsBlock({ label, counts, accent }) {
  const rows = [
    ["signals", counts.signals],
    ["findings", counts.findings],
    ["triangulation runs", counts.triangulation_runs],
    ["perplexity runs", counts.perplexity_runs],
    ["gap cells", counts.gap_cells],
    ["actions", counts.actions],
    ["deliverables", counts.deliverables],
    ["methodology", counts.methodology],
    ["sources", counts.sources],
  ];
  return (
    <div className="border border-[#E8E1D2] bg-white p-3.5">
      <div
        className="font-mono text-[9.5px] uppercase tracking-[0.22em] mb-2"
        style={{ color: accent }}
      >
        {label}
      </div>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between text-[12px]">
            <dt className="text-[#5C6470]">{k}</dt>
            <dd className="text-[#0F1825] font-mono">{v ?? 0}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
